import React, { useContext, useState } from "react";
import { ModalContext } from "../../utils/Context";
import { postApi } from "../../utils/services";
import {
    REJECT_INDIVIDUAL_BILL_API,
    REJECT_PETTY_CASH,
    REJECT_PURCHASE_CONTRACT,
    REJECTED_BILL_API,
} from "../../utils/apiRoutes";
import toast from "react-hot-toast";
import { useTransactionContext } from "../../utils/helpers/useTransaction";
import { useLocation, useNavigate } from "react-router-dom";
import Alert from "./Alert";

const RejectedAtModal = ({
    data,
    levelValue,
    handlerList,
    rejectionType,
    pageNumber,
    pageSize,
    globalSearch,
    billStatusId,
    billType
}) => {
    const [rejectReason, setRejectReason] = useState("");
    const [level, setLevel] = useState("");
    const modalContext = useContext(ModalContext);
    const { handleModalData } = modalContext;
    const user = JSON.parse(localStorage.getItem("userData"));
    const transactionList = useTransactionContext();
    const [errors, setErrors] = useState({});
    const { closeModal } = modalContext;
    const [disable, setDisable] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const isBulk = Array.isArray(data);
    const currentData = isBulk ? data[0] : data;
    const isPettyCash = billType === 1;
    const isStandaloneBill = currentData?.IndividualBill === true;
    const isPurchaseContract = currentData?.IndividualBill === undefined;

    const handleRole = (e) => {
        setLevel(e.target.value);
        setErrors((prev) => ({ ...prev, role: "" }));
    };

    const handleRejectionReason = (e) => {
        const value = e.target.value;
        if (value.trim() === "" && value.length > 0) {
            return;
        }
        setRejectReason(value);
        setErrors((prev) => ({ ...prev, rejectionReason: "" }));
    };

    const validateModal = () => {
        let errors = {};
        let isModalValid = true;

        if (!rejectReason) {
            errors.rejectionReason = "Please provide a rejection reason!";
            isModalValid = false;
        }
        if (levelValue.length > 0 && rejectionType === "RejectAt" && !level) {
            errors.role = "Select a role!";
            isModalValid = false;
        }

        setErrors(errors);
        return isModalValid;
    };

    const getBillLabel = () => {
        if (isPettyCash) return "Petty Cash";
        if (isPurchaseContract) return "Purchase Contract";
        if (isStandaloneBill) return "Standalone Bill";
        return "Bill";
    };

    const handleReject = async () => {
        try {
            const isValid = validateModal();
            if (!isValid) return;

            setDisable(true);

            const transactionId = isPettyCash
                ? transactionList[5]?.id
                : isPurchaseContract
                    ? transactionList[2]?.id
                    : isStandaloneBill
                        ? transactionList[4]?.id
                        : transactionList[1]?.id;

            if (!transactionId) {
                throw new Error("Invalid transaction type");
            }

            const payload = {
                transactionId,
                roleId: user.RoleId,
                rejectionReason: rejectReason,
                level: levelValue.length > 0 && rejectionType === "RejectAt" ? Number(level) : "",
            };

            if (isPettyCash) {
                payload.billId = currentData.id
            } else if (isPurchaseContract) {
                payload.contractId = isBulk ? data.map((item) => item.id) : [currentData.id];
            } else {
                payload.billId = isBulk ? data.map((item) => item.id) : [currentData.id];
            }

            const apiRoute = isPettyCash
                ? REJECT_PETTY_CASH
                : isPurchaseContract
                    ? REJECT_PURCHASE_CONTRACT
                    : isStandaloneBill
                        ? REJECT_INDIVIDUAL_BILL_API
                        : REJECTED_BILL_API;

            const result = await postApi(apiRoute, payload);

            if (result?.res?.success) {
                toast.success(result.res.message);

                if (typeof handlerList === "function") {
                    handlerList(pageNumber, pageSize, globalSearch, billStatusId);
                }

                closeModal();

                const path = location.pathname;
                if (path === "/") {
                    navigate("/");
                } else if (path.includes("petty-cash-listing")) {
                    navigate("/petty-cash-listing");
                } else if (path.includes("contract-listing")) {
                    navigate("/contract-listing");
                } else if (path.includes("invoice-listing")) {
                    navigate("/invoice-listing");
                } else if (path.includes("individual-bill-list")) {
                    navigate("/individual-bill-list");
                } else {
                    navigate(-1);
                }
            } else if (result?.res?.holdMessage) {
                const alert = <Alert message={result.res.holdMessage} />;
                handleModalData(alert, "md");
            }
        } catch (error) {
            toast.error(error.message || "Something went wrong!");
        } finally {
            setDisable(false);
        }
    };

    return (
        <div className="cstm-frm-mdl">
            <div className="modal-header">
                <h4 className="modal-title">Rejection Reason</h4>
            </div>

            <div className="modal-body">
                <label className="form-label">
                    Are you sure you want to reject {isBulk ? "these" : "this"} {getBillLabel()}? This action cannot be reverted.
                </label>

                {levelValue.length > 0 && rejectionType === "RejectAt" && (
                    <div className="form-group mb-20 mt-2 position-relative">
                        <select value={level} className="form-select" onChange={handleRole}>
                            <option value="">Select</option>
                            {levelValue?.map((option, index) => (
                                <option key={index} value={option?.level}>
                                    {option?.RoleName}
                                </option>
                            ))}
                        </select>
                        {errors.role && <span className="validation-error">{errors.role}</span>}
                    </div>
                )}

                <div className="form-group">
                    <textarea
                        value={rejectReason}
                        className="form-control reject-modal-text-area"
                        placeholder="Enter the reason here..."
                        rows={3}
                        maxLength={100}
                        onChange={handleRejectionReason}
                    />
                    {errors.rejectionReason && (
                        <p className="validation-error">{errors.rejectionReason}</p>
                    )}
                </div>
            </div>

            <div className="modal-footer">
                <div className="d-flex align-items-center mb-20">
                    <div className="danger-outline-btn">
                        <button type="button" className="btn" onClick={closeModal}>
                            Cancel
                        </button>
                    </div>
                    <div className="danger-btn ms-2">
                        <button type="submit" className="btn" onClick={handleReject} disabled={disable}>
                            Reject
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RejectedAtModal;